import { DataBagService } from '@/common/data-bag.service';
import { InfoboxService } from '@/common/infobox/infobox.service';
import { QuestionService } from '@/common/question-dialog/question-dialog.service';
import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
	BehaviorSubject,
	debounceTime,
	distinctUntilChanged,
	filter,
	from,
	of,
	Subject,
	switchMap,
	takeUntil,
} from 'rxjs';
import { Organization, Result } from '../../../reservation.model';
import { ReservationService } from '../../../reservation.service';

const QUESTION_BOX_KEY = 'deleteAsk';
@Component({
	selector: 'app-reservation-summary-agent-dialog',
	templateUrl: './reservation-summary-agent-dialog.component.html',
	styleUrls: ['./reservation-summary-agent-dialog.component.scss'],
})
export class ReservationSummaryAgentDialogComponent implements OnInit, OnDestroy {
	loading = false;
	searchType: 'N' | 'C' | 'M' = 'N'; // N - name, C - city, M - merlinX
	selectedSeller = null;
	filteredSellers: Organization[];
	sellerSelect = new FormControl();
	isSelectedSeller$ = new BehaviorSubject<boolean>(true);

	private readonly api = inject(DataBagService);
	private readonly _service = inject(ReservationService);
	private readonly infobox = inject(InfoboxService);
	private readonly questionBox = inject(QuestionService);
	private readonly id = inject<number>(MAT_DIALOG_DATA);
	private readonly dialogRef = inject<MatDialogRef<ReservationSummaryAgentDialogComponent>>(MatDialogRef);
	private readonly destroy$ = new Subject<void>();

	private sellerCategory: number = null;

	ngOnInit(): void {
		this.sellerSelect.valueChanges
			.pipe(
				debounceTime(400),
				distinctUntilChanged(),
				switchMap((searchTerm: string) => {
					if (searchTerm === '') {
						this.filteredSellers = [];
						this.selectedSeller = null;
						this.sellerCategory = null;
						this.isSelectedSeller$.next(true);
						return of([]);
					}
					return this.filterSellers(searchTerm);
				}),
			)
			.subscribe((filtered: Result<Organization>) => {
				this.filteredSellers = filtered.results;
			});
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	displayFn(item: number): string {
		return item ? this.filteredSellers.find((v) => v.id === item).name : '';
	}

	selectSeller($event: MatAutocompleteSelectedEvent): void {
		this.selectedSeller = $event.option.value;
		this.sellerCategory = this.filteredSellers.find((v) => v.id === this.selectedSeller)?.sellCategory;
		this.isSelectedSeller$.next(false);
	}

	submit(): void {
		this.questionBox.ask('Czy jesteś pewny, że chcesz zmienić agenta?', QUESTION_BOX_KEY);

		this.questionBox.status
			.pipe(
				filter((result) => result.dialogId === QUESTION_BOX_KEY && result.result),
				takeUntil(this.destroy$),
			)
			.subscribe(() => this.changeSeller());
	}

	closeDialog(): void {
		this.dialogRef.close();
	}

	private filterSellers(searchTerm: string) {
		return from(
			new Promise((resolve, reject) => {
				if (typeof searchTerm === 'number' || searchTerm.length < 3) return;

				const url = this.getUrl().concat(searchTerm);
				this.loading = true;
				this.filteredSellers = [];
				this.api.get(url, (response, error) => {
					this.loading = false;
					if (error) reject(error);
					else resolve(response);
				});
			}),
		);
	}

	private getUrl(): string {
		let url = `/organization/?`;

		url =
			this.searchType === 'N'
				? url.concat(`&name=`)
				: this.searchType === 'C'
					? url.concat('&address_location_string=')
					: url.concat('&code_merlinx=');

		return url;
	}

	private changeSeller(): void {
		const payload = {
			seller: this.selectedSeller,
			sellerCategory: this.sellerCategory,
		};

		this.api.patch(`/reservation/${this.id}/`, payload, (result, error) => {
			if (error) return;
			else {
				this.infobox.success('Pomyślnie zaktualizowano agenta!');
				this._service.loadReservation(this.id);
				this.dialogRef.close();
			}
		});
	}
}
